import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { setSeo } from "@/lib/seo";
import useSettings from "@/hooks/useSettings";
import useUiTexts, { uiText } from "@/lib/uiTexts";
import { GALLERY_DEFAULTS } from "@/lib/collections";

const BRAND = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const settings = useSettings();
  const T = useUiTexts();
  const [products, setProducts] = useState([]);
  const [pieceCount, setPieceCount] = useState(null);
  const [home, setHome] = useState({});
  const [gallery, setGallery] = useState([]);
  const isPrelaunch = settings.sales_stage === "exhibicion";

  useEffect(() => {
    setSeo({ title: "", description: "Un museo de piezas de archivo. Moda de autor de Camila Guerra en ediciones limitadas. Construí tu propio archivo.", path: "/" });
    api.get("/products").then((r) => { setProducts(r.data.slice(0, 6)); setPieceCount(r.data.filter((p) => !p.vip).length); }).catch(() => {});
    api.get("/content/home").then((r) => setHome(r.data)).catch(() => {});
    api.get("/content/gallery").then((r) => setGallery((r.data.images || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO — en prelanzamiento es la portada de la exhibición */}
      <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden">
        <img
          data-testid="hero-image"
          className="absolute inset-0 w-full h-full object-cover"
          src={home.hero_image || "/hero/foto1.webp"}
          alt="Archive Lab - digital fashion exhibition"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/65 to-[#0A0A0A]/45" />
        <div className="relative z-10 h-full flex flex-col justify-end px-4 sm:px-8 lg:px-16 pb-16 lg:pb-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}>
            <div className="dossier-label text-[#B9BCC2] mb-4">{isPrelaunch ? uiText(T, "hero_eyebrow") : "Archivo → Pieza → Expediente → Edición → Liberación"}</div>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-[#F4F4F3] text-4xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
              {home.hero_title || "Construí tu propio archivo"}
            </h1>
            <p className="mt-5 text-[#B9BCC2] text-base sm:text-lg max-w-xl leading-relaxed">
              {isPrelaunch
                ? (home.hero_subtitle || "Una exhibición digital de piezas únicas intervenidas. Elegí las que te gusten y tocá «Quiero esta pieza» para tener acceso antes cuando se abra la edición.")
                : (home.hero_subtitle || "No necesitás más ropa. Necesitás mejores piezas. Moda de autor en ediciones limitadas, pensada para quedarse.")}
            </p>
            {isPrelaunch ? (<>
              <div data-testid="early-access-steps" className="mt-7 max-w-xl border-l-2 border-[#171719] pl-4">
                <div className="dossier-label text-[#565A61] mb-2">{uiText(T, "early_title")}</div>
                <p className="text-sm text-[#E8E9EB] leading-relaxed">{uiText(T, "early_text")}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-5 items-center" data-testid="prelaunch-cta">
                <Link to="/archivo" data-testid="explore-archive-cta" className="bg-[#D9DADD] text-[#171719] hover:bg-[#F4F4F3] px-8 py-4 inline-flex items-center gap-3 dossier-label transition-colors">
                  {uiText(T, "hero_cta")} <ArrowRight size={16} />
                </Link>
                <span data-testid="piece-count" className="dossier-label text-[10px] text-[#B9BCC2]">{pieceCount ?? "…"} {uiText(T, "pieces_count")}</span>
              </div>
            </>) : (
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/archivo" data-testid="explore-archive-cta" className="btn-ink px-8 py-4 inline-flex items-center gap-3 dossier-label">
                  {uiText(T, "hero_cta_venta")} <ArrowRight size={16} />
                </Link>
                {settings.creator_enabled && <Link to="/camila-guerra" data-testid="vip-cta" className="btn-outline-ink px-8 py-4 inline-flex items-center gap-3 dossier-label">
                  Cami Guerra Archivo VIP
                </Link>}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* GALERÍA EDITORIAL 3+3 (fotos administrables desde el panel) */}
      {GALLERY_DEFAULTS.some((d, i) => (gallery[i] || d)) && (
        <section data-testid="home-gallery" className="grid grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => {
            const src = gallery[i] || GALLERY_DEFAULTS[i];
            return src ? <img key={i} data-testid={`gallery-img-${i}`} src={src} alt="Archive Lab" loading="lazy"
              className="w-full aspect-square object-cover hover:opacity-90 transition-opacity" /> : null;
          })}
        </section>
      )}

      {!isPrelaunch && (<>
      {/* MANIFESTO */}
      <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28 border-b border-[#C9CBD1]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="dossier-label text-[#565A61] mb-4">{uiText(T, "concept_eyebrow")}</div>
            <h2 className="font-display font-bold uppercase tracking-tight text-3xl lg:text-4xl text-[#171719] leading-tight">
              {uiText(T, "concept_title")}
            </h2>
          </div>
          <div className="lg:col-span-7 text-[#565A61] text-base lg:text-lg leading-relaxed space-y-4">
            <p>{home.manifesto || "Archive Lab funciona como un museo. Cada drop es una liberación. Cada prenda tiene su propia identidad. Cada edición es limitada. Cada adquisición pasa a formar parte del archivo personal de quien la compra."}</p>
            <p className="font-script text-3xl text-[#565A61]">{uiText(T, "manifesto_script")}</p>
          </div>
        </div>
      </section>

      {/* LATEST PIECES */}
      <section className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="dossier-label text-[#565A61] mb-2">{uiText(T, "latest_eyebrow")}</div>
            <h2 className="font-display font-bold uppercase tracking-tight text-3xl lg:text-4xl text-[#171719]">{uiText(T, "latest_title")}</h2>
          </div>
          <Link to="/archivo" className="dossier-label text-[#565A61] hover:text-[#171719] transition-colors inline-flex items-center gap-2">
            {uiText(T, "latest_link")} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {products.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      {/* BELLEZA DE LOS OPUESTOS (ocultable desde Ajustes → style_mix_enabled) */}
      {settings.style_mix_enabled !== false && (
      <section className="px-4 sm:px-8 lg:px-16 py-20 border-t border-[#C9CBD1]">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          {["Clásico + inesperado", "Formal + rebelde", "Femenino + poderoso", "Minimalista + statement", "Sofisticado + canchero"].map((t) => (
            <div key={t} className="museum-frame p-6 hover-lift">
              <span className="font-display font-semibold uppercase tracking-tight text-[#171719] text-sm">{t}</span>
            </div>
          ))}
        </div>
        <p className="text-center font-script text-3xl text-[#565A61] mt-10">{uiText(T, "mix_script")}</p>
      </section>
      )}
      </>)}
    </div>
  );
}
